import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  TrendingUp, 
  TrendingDown, 
  Lock, 
  ArrowUpRight,
  Loader2,
  Filter,
  Crown,
  Minus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useNotifications from '../hooks/useNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Signals = () => {
  const navigate = useNavigate();
  const { token, canViewSignal, isPremium, isAdmin } = useAuth();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [showPremiumOnly, setShowPremiumOnly] = useState(false);
  
  // Notifications
  const { notifyNewSignal } = useNotifications();
  const knownSignalIds = useRef(new Set());
  const isFirstLoad = useRef(true);

  useEffect(() => {
    fetchSignals();
    
    // Set up polling for new signals every 30 seconds
    const pollInterval = setInterval(() => {
      fetchSignals();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, [token]);

  const fetchSignals = async () => {
    try {
      const response = await axios.get(`${API}/trading_setups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newSignals = response.data || [];
      
      // Check for new signals and send notifications
      const notificationsEnabled = localStorage.getItem('coinpulse_notifications') === 'true';
      
      if (!isFirstLoad.current && notificationsEnabled) {
        for (const signal of newSignals) {
          const signalId = signal._id || signal.id;
          if (!knownSignalIds.current.has(signalId)) {
            // New signal detected!
            notifyNewSignal(signal);
            toast.success(`New signal: ${signal.symbol}`, {
              description: signal.title || signal.bias || 'New trading opportunity',
            });
          }
        }
      }
      
      // Update known signal IDs
      knownSignalIds.current = new Set(newSignals.map(s => s._id || s.id));
      isFirstLoad.current = false;
      
      setSignals(newSignals);
    } catch (error) {
      console.error('Failed to fetch signals:', error);
      if (loading) {
        toast.error('Failed to load signals');
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to determine direction from bias field
  const getDirection = (signal) => {
    const bias = signal.bias?.toLowerCase() || '';
    if (bias.includes('long') || bias.includes('bullish') || bias.includes('buy')) {
      return 'LONG';
    }
    if (bias.includes('short') || bias.includes('bearish') || bias.includes('sell')) {
      return 'SHORT';
    }
    if (bias.includes('neutral') || bias.includes('monitoring') || bias.includes('watch')) {
      return 'NEUTRAL';
    }
    return signal.bias || 'N/A';
  };

  const isLongSignal = (signal) => {
    const direction = getDirection(signal);
    return direction === 'LONG';
  };

  const isNeutralSignal = (signal) => {
    const direction = getDirection(signal);
    return direction === 'NEUTRAL';
  };

  const filteredSignals = useMemo(() => {
    return signals
      .filter(signal => {
        const matchesSearch = signal.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             signal.title?.toLowerCase().includes(searchQuery.toLowerCase());
        const direction = getDirection(signal);
        const matchesDirection = directionFilter === 'all' || 
          (directionFilter === 'long' && isLongSignal(signal)) ||
          (directionFilter === 'short' && !isLongSignal(signal) && direction !== 'N/A');
        const matchesPremiumFilter = !showPremiumOnly || Boolean(signal.Premium);
        return matchesSearch && matchesDirection && matchesPremiumFilter;
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [signals, searchQuery, directionFilter, showPremiumOnly]);

  const SignalCard = ({ signal }) => {
    const isLong = isLongSignal(signal);
    const isNeutral = isNeutralSignal(signal);
    const direction = getDirection(signal);
    const signalIsPremium = Boolean(signal.Premium);
    const canView = canViewSignal(signal);
    const signalId = signal._id || signal.id;

    // Get entry price (use entry_zone_min or entry_zone_max)
    const entryPrice = signal.entry_zone_min || signal.entry_zone_max;
    // Get stop loss (use defensive_sl or aggressive_sl)
    const stopLoss = signal.defensive_sl || signal.aggressive_sl;
    // Get take profit
    const takeProfit = signal.tp1;

    // Format timestamp
    const formatTimestamp = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    // Neutral signals are smaller and grayed out with orange icon
    if (isNeutral) {
      return (
        <Card 
          className="border-[#1F1F1F]/50 bg-[#0A0A0A]/50 hover:border-[#F97316]/30 transition-all duration-300 cursor-pointer opacity-70 hover:opacity-90"
          onClick={() => navigate(`/signals/${signalId}`)}
          data-testid={`signal-card-${signalId}`}
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center bg-[#F97316]/10">
                  <Minus className="w-4 h-4 text-[#F97316]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#A1A1AA] text-sm font-mono">{signal.symbol}</h3>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[#F97316]">
                    {direction}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-right">
                <div className="flex flex-col items-end">
                  <Badge className="bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20 font-mono text-[10px] uppercase tracking-wider">
                    Monitoring
                  </Badge>
                  <span className="text-[9px] text-[#737373] mt-1 font-mono">
                    {formatTimestamp(signal.created_at)}
                  </span>
                </div>
                {signalIsPremium && (
                  <Crown className="w-3 h-3 text-[#6366F1]/50" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card 
        className={`border-[#1F1F1F] bg-[#0A0A0A] hover:border-[#00FF94]/30 transition-all duration-300 cursor-pointer group ${
          signalIsPremium && !canView ? 'premium-border' : ''
        }`}
        onClick={() => navigate(`/signals/${signalId}`)}
        data-testid={`signal-card-${signalId}`}
      >
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isLong ? 'bg-[#00FF94]/10' : 'bg-[#FF0055]/10'
              }`}>
                {isLong ? (
                  <TrendingUp className="w-5 h-5 text-[#00FF94]" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-[#FF0055]" />
                )}
              </div>
              <div>
                <h3 className="font-bold text-white text-lg font-mono">{signal.symbol}</h3>
                <span className={`text-xs font-mono uppercase tracking-wider ${
                  isLong ? 'text-[#00FF94]' : 'text-[#FF0055]'
                }`}>
                  {direction}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {signalIsPremium ? (
                <Badge className="bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/20 font-mono text-xs uppercase tracking-wider hover:bg-[#6366F1]/20">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium
                </Badge>
              ) : (
                <Badge className="bg-white/5 text-white/60 border border-white/10 font-mono text-xs uppercase tracking-wider hover:bg-white/10">
                  Free
                </Badge>
              )}
            </div>
          </div>

          {/* Title if available */}
          {signal.title && (
            <p className="text-sm text-[#A1A1AA] mb-3 line-clamp-1">{signal.title}</p>
          )}

          {/* Content */}
          {canView ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-black/50 rounded-md p-2">
                  <p className="text-[10px] text-[#737373] uppercase tracking-wider mb-1">Entry</p>
                  <p className="font-mono text-white text-sm">{entryPrice || 'N/A'}</p>
                </div>
                <div className="bg-black/50 rounded-md p-2">
                  <p className="text-[10px] text-[#737373] uppercase tracking-wider mb-1">Stop Loss</p>
                  <p className="font-mono text-[#FF0055] text-sm">{stopLoss || 'N/A'}</p>
                </div>
                <div className="bg-black/50 rounded-md p-2">
                  <p className="text-[10px] text-[#737373] uppercase tracking-wider mb-1">Take Profit</p>
                  <p className="font-mono text-[#00FF94] text-sm">{takeProfit || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-[#1F1F1F]">
                <div className="flex items-center gap-2">
                  {signal.risk_reward && (
                    <Badge 
                      variant="outline" 
                      className="text-xs font-mono border-[#00FF94]/30 text-[#00FF94]"
                    >
                      R:R {signal.risk_reward}
                    </Badge>
                  )}
                  {signal.volatility && (
                    <Badge 
                      variant="outline" 
                      className="text-xs font-mono border-[#737373]/30 text-[#737373]"
                    >
                      {signal.volatility}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-[10px] text-[#737373] font-mono">
                    {signal.created_at ? new Date(signal.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : ''}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-[#737373] group-hover:text-[#00FF94] transition-colors" />
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              {/* Blurred placeholder */}
              <div className="premium-blur select-none pointer-events-none">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-black/50 rounded-md p-2">
                    <p className="text-[10px] text-[#737373] uppercase tracking-wider mb-1">Entry</p>
                    <p className="font-mono text-white text-sm">$0,000.00</p>
                  </div>
                  <div className="bg-black/50 rounded-md p-2">
                    <p className="text-[10px] text-[#737373] uppercase tracking-wider mb-1">Stop Loss</p>
                    <p className="font-mono text-[#FF0055] text-sm">$0,000.00</p>
                  </div>
                  <div className="bg-black/50 rounded-md p-2">
                    <p className="text-[10px] text-[#737373] uppercase tracking-wider mb-1">Take Profit</p>
                    <p className="font-mono text-[#00FF94] text-sm">$0,000.00</p>
                  </div>
                </div>
              </div>
              {/* Lock overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0A]/80">
                <Lock className="w-6 h-6 text-[#6366F1] mb-2" />
                <p className="text-xs text-[#A1A1AA] mb-2">Premium Signal</p>
                <Button 
                  size="sm" 
                  className="bg-[#6366F1] hover:bg-[#6366F1]/90 text-white text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    toast.info('Upgrade to Premium to unlock this signal');
                  }}
                  data-testid="upgrade-btn"
                >
                  Upgrade to Unlock
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in-up">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Trading Signals
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-1">
              {filteredSignals.length} signal{filteredSignals.length !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <Input
              placeholder="Search by symbol or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="search-input"
            />
          </div>
          
          <Select value={directionFilter} onValueChange={setDirectionFilter}>
            <SelectTrigger className="w-full sm:w-[140px] bg-black border-[#1F1F1F] text-white" data-testid="direction-filter">
              <Filter className="w-4 h-4 mr-2 text-[#737373]" />
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#1F1F1F]">
              <SelectItem value="all" className="text-white focus:bg-white/5 focus:text-white">All Directions</SelectItem>
              <SelectItem value="long" className="text-[#00FF94] focus:bg-[#00FF94]/10 focus:text-[#00FF94]">Long / Bullish</SelectItem>
              <SelectItem value="short" className="text-[#FF0055] focus:bg-[#FF0055]/10 focus:text-[#FF0055]">Short / Bearish</SelectItem>
            </SelectContent>
          </Select>

          {(isPremium || isAdmin) && (
            <Button
              variant={showPremiumOnly ? 'default' : 'outline'}
              onClick={() => setShowPremiumOnly(!showPremiumOnly)}
              className={showPremiumOnly 
                ? 'bg-[#6366F1] hover:bg-[#6366F1]/90 text-white' 
                : 'border-[#1F1F1F] bg-transparent hover:bg-white/5 text-white'
              }
              data-testid="premium-filter-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Premium Only
            </Button>
          )}
        </div>

        {/* Signals Grid */}
        {filteredSignals.length === 0 ? (
          <div className="text-center py-16 border border-[#1F1F1F] rounded-lg bg-[#0A0A0A]">
            <TrendingUp className="w-12 h-12 text-[#737373] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No signals found</h3>
            <p className="text-sm text-[#737373]">
              {searchQuery || directionFilter !== 'all'
                ? 'Try adjusting your filters' 
                : 'Check back later for new signals'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSignals.map((signal) => (
              <SignalCard key={signal._id || signal.id} signal={signal} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Signals;
